import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Upload, X, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { documentosAPI } from '../services/api';

/**
 * Componente para subir PDFs directamente a Cloudflare R2
 * El archivo va directo del navegador a Cloudflare, sin pasar por nuestro servidor
 */
const R2FileUpload = ({ 
  documentId,
  onSuccess,
  onError,
  maxSize = 750, // MB - R2 soporta hasta 5GB pero limitamos a 750MB
  disabled = false 
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);
  const abortController = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validar tipo
    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    // Validar tamaño
    const sizeMB = selectedFile.size / (1024 * 1024);
    if (sizeMB > maxSize) {
      toast.error(`El archivo es muy grande. Máximo: ${maxSize} MB`);
      return;
    }

    setFile(selectedFile);
    setError('');
    setSuccess(false);
    setProgress(0);
  };

  const handleUpload = async () => {
    if (!file || !documentId) return;

    setUploading(true);
    setError('');
    setProgress(0);
    abortController.current = new AbortController();

    try {
      // Paso 1: Obtener URL pre-firmada del servidor (5%)
      setStatusMessage('Preparando subida...');
      setProgress(5);

      const formData = new FormData();
      formData.append('filename', file.name);
      formData.append('content_type', 'application/pdf');

      const presignedResponse = await documentosAPI.getR2PresignedUrl(documentId, formData);
      const { upload_url, file_key } = presignedResponse.data;

      // Paso 2: Subir directamente a Cloudflare R2 (5% - 90%)
      setStatusMessage('Subiendo a Cloudflare...');
      
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 85) + 5;
            setProgress(percentComplete);
            const mbUploaded = (event.loaded / (1024 * 1024)).toFixed(1);
            const mbTotal = (event.total / (1024 * 1024)).toFixed(1);
            setStatusMessage(`Subiendo: ${mbUploaded} MB de ${mbTotal} MB`);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Error de subida: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Error de red al subir'));
        xhr.onabort = () => reject(new Error('Subida cancelada'));

        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('Content-Type', 'application/pdf');
        xhr.send(file);

        // Guardar referencia para poder cancelar
        abortController.current.signal.addEventListener('abort', () => xhr.abort());
      });

      // Paso 3: Confirmar subida al servidor (90% - 100%)
      setStatusMessage('Confirmando subida...');
      setProgress(95);

      const confirmFormData = new FormData();
      confirmFormData.append('file_key', file_key);
      
      const confirmResponse = await documentosAPI.confirmR2Upload(documentId, confirmFormData);

      setProgress(100);
      setSuccess(true);
      setStatusMessage('¡PDF subido correctamente!');
      toast.success(`PDF subido correctamente (${confirmResponse.data.size_mb} MB)`);

      if (onSuccess) {
        onSuccess({
          file_key,
          size_mb: confirmResponse.data.size_mb
        });
      }

    } catch (err) {
      console.error('Error en upload R2:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Error al subir el archivo';
      setError(errorMessage);
      toast.error(errorMessage);
      
      if (onError) {
        // Pasar solo el mensaje, no el objeto Error (evita crash de React)
        onError(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (abortController.current) {
      abortController.current.abort();
    }
    setUploading(false);
    setProgress(0);
    setStatusMessage('');
  };

  const handleReset = () => {
    setFile(null);
    setError('');
    setSuccess(false);
    setProgress(0);
    setStatusMessage('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const fileSizeMB = file ? (file.size / (1024 * 1024)).toFixed(2) : 0;

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-r from-sky-50 to-blue-50" data-testid="r2-file-upload">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="p-2 bg-sky-100 rounded-lg">
          <Upload className="h-5 w-5 text-sky-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-800">Subir PDF</h3>
          <p className="text-xs text-gray-500">Archivos de hasta {maxSize} MB</p>
        </div>
      </div>

      {/* File Input */}
      {!file && !success && (
        <div className="border-2 border-dashed border-sky-200 rounded-lg p-6 text-center hover:border-sky-400 transition-colors">
          <input
            ref={inputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            disabled={disabled || uploading}
            className="hidden"
            id={`r2-file-input-${documentId}`}
          />
          <label 
            htmlFor={`r2-file-input-${documentId}`}
            className="cursor-pointer"
          >
            <FileText className="h-12 w-12 mx-auto text-sky-400 mb-2" />
            <p className="text-sm text-gray-600">
              Hacé clic o arrastrá un archivo PDF
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Máximo {maxSize} MB
            </p>
          </label>
        </div>
      )}

      {/* Selected File Info */}
      {file && !success && (
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-sky-500" />
            <div>
              <p className="font-medium text-sm truncate max-w-[200px]">{file.name}</p>
              <p className="text-xs text-gray-500">{fileSizeMB} MB</p>
            </div>
          </div>
          {!uploading && (
            <Button variant="ghost" size="sm" onClick={handleReset}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {/* Progress */}
      {uploading && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{statusMessage}</span>
            <span className="font-medium">{progress}%</span>
          </div>
          <Progress value={progress} className="h-3" />
          <Button variant="outline" size="sm" onClick={handleCancel} className="w-full">
            Cancelar
          </Button>
        </div>
      )}

      {/* Success State */}
      {success && (
        <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
          <CheckCircle className="h-8 w-8 text-green-500" />
          <div>
            <p className="font-medium text-green-700">¡Subida exitosa!</p>
            <p className="text-sm text-green-600">{file?.name} ({fileSizeMB} MB)</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleReset} className="ml-auto">
            Subir otro
          </Button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
          <AlertCircle className="h-6 w-6 text-red-500" />
          <div className="flex-1">
            <p className="font-medium text-red-700">Error</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleReset}>
            Reintentar
          </Button>
        </div>
      )}

      {/* Upload Button */}
      {file && !uploading && !success && !error && (
        <Button 
          onClick={handleUpload} 
          disabled={disabled}
          className="w-full bg-sky-500 hover:bg-sky-600"
        >
          <Upload className="h-4 w-4 mr-2" />
          Subir PDF ({fileSizeMB} MB)
        </Button>
      )}
    </div>
  );
};

export default R2FileUpload;
